import { Descriptions, Select } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { CgCloseR } from "react-icons/cg";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import useNotification from "../../hooks/useNotification";
import { IArchive } from "../../models/archives.model";
import { RootState } from "../../store/rootReducer";
import Button from "../Button";
import FileUpload from "../FileUpload";
import ConfirmModal from "../modals/ConfirmModal";
import Tinymce from "../Tinymce";

interface IProps {
  codeId: string;
  archiveCode?: number;
  data?: IArchive|undefined;
  submit: (data: FormData) => void;
}

const ArchiveEditor: FC<IProps> = ({
  codeId,
  archiveCode,
  data,
  submit,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const commonCode = commonCodes.find(item => item.commonCode === codeId)?.detailCode;
  const { infoNotification } = useNotification();

  const [archiveCategoryCode, setArchiveCategoryCode] = useState<string>();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [files, setFiles] = useState<any>([null]);
  const [readCount, setReadCount] = useState(0);
  const [isCreate, setIsCreate] = useState(true);
  const [archiveFiles, setArchiveFiles] = useState<any>([]);
  const [confirmCancelVisible, setConfirmCancelVisible] = useState(false);

  useEffect(() => {
    if (auth?.hqYn === 'N') {
      history.push('/');
    }
  }, [auth]);

  useEffect(() => {
    if (data) {
      setArchiveCategoryCode(data.archiveCategoryCode);
      setTitle(data.title);
      setContent(data.content);
      setReadCount(data.readCount);
      setArchiveFiles(data.archiveFiles);
      setIsCreate(false);
    }
  }, [data]);

  const onSetFile = (index: number, file: any) => {
    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  }

  const onAddFile = () => {
    const _files = [...files];
    _files.push(null);
    setFiles(_files);
  }

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  }

  const onSubmit = () => {
    if (!archiveCategoryCode) {
      return infoNotification(t('archive.learn.message1'));
    }
    if (!title) {
      return infoNotification(t('student.student.message15'));
    }
    if (!content) {
      return infoNotification(t('student.student.message56'));
    }

    const form = new FormData();
    form.append('archiveCategoryCode', archiveCategoryCode);
    form.append('title', title);
    form.append('content', content);
    if (archiveCode) form.append('archiveCode', String(archiveCode));

    let fileAttached = false;
    let isAllDeleted = true;

    if (isCreate) {
      /* 생성 시 파일 처리 */
      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) {
            form.append('archiveFiles', item);
            fileAttached = true;
          }
        });
      }
    } else {
      /* 수정 시 파일 처리 */
      if (data?.archiveFileCode) {
        form.append('archiveFileCode', String(data.archiveFileCode));
      }

      if (archiveFiles) {
        // 기존 첨부파일 있음
        let hasNewFiles = false;
        files.forEach((item: any) => {
          if (item) hasNewFiles = true;
        });

        let hasDeleted = false;
        archiveFiles.forEach((item: any) => {
          if (item.originalName) {
            isAllDeleted = false;
          } else {
            hasDeleted = true;
            form.append('deleteArchiveFiles', item.fileSeq);
          }
        });

        if (hasNewFiles) form.append('archiveFileStatus', 'update');
        else if (isAllDeleted) form.append('archiveFileStatus', 'delete');
        else if (hasDeleted) form.append('archiveFileStatus', 'update');
        else form.append('archiveFileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (files.length > 0 && files[0] !== null) {
          // 추가 첨부파일 있음
          form.append('archiveFileStatus', 'create');
        } else {
          // 추가 첨부파일 없음
          form.append('archiveFileStatus', 'keep');
        }
      }

      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) {
            form.append('archiveNewFiles', item);
            fileAttached = true;
          }
        });
      }      
    }

    if (!fileAttached && isAllDeleted) {
      return infoNotification(t('archive.learn.message2'));
    }

    submit(form);    
  }

  const onDeleteAttachedFile = (index: number) => {
    const _archiveFiles = [...archiveFiles];
    _archiveFiles[index].originalName = null;
    setArchiveFiles(_archiveFiles);
  }

  const onCancel = () => {
    if (codeId === 'BR0010') {
      history.push(`/archive/learn${location.search}`)
    } else {
      history.push(`/archive/manage${location.search}`)
    }
  }

  return (
    <div className='comm-table__area rowType'>
      <Descriptions
        bordered
        column={4}
        size="small"
        labelStyle={{ width: '12.5%' }}
        contentStyle={{ width: '12.5%' }}
      >
        <Descriptions.Item label={`* ${t('archive.exam.column2')}`}>
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('archive.exam.column2')}
              value={archiveCategoryCode}
              onChange={value => setArchiveCategoryCode(value)}
            >
              {
                commonCode?.map((item: any) => {
                  return (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {i18n.language === 'ko' ? item.korName : item.engName}
                    </Select.Option>
                  )
                })
              }
            </Select>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('common.column.writer')}>
          {auth?.korName}
        </Descriptions.Item>
        <Descriptions.Item label={t('archive.learn.column4')}>
          {moment(serverDate).format('YYYY.MM.DD')}
        </Descriptions.Item>
        <Descriptions.Item label={t('archive.learn.column3')}>
          {readCount}
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('common.column.title')}`} span={4}>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('common.column.contents')}`} span={4}>
          <Tinymce
            content={content}
            setContent={setContent}
          />          
        </Descriptions.Item>
        {
          archiveFiles?.length
            ? (
              <Descriptions.Item label={t('archive.learn.column5')} span={4}>
                {
                  archiveFiles.map((item: any, index: number) => {
                    return item?.originalName
                      ? (
                        <div>
                          <a
                            className="download-link"
                            href={`${item.fileLocation}`}
                            download={item.originalName}
                          >
                            {item.originalName}
                          </a>
                          <span
                            className="ml-1 pointer"
                            onClick={() => onDeleteAttachedFile(index)}
                          >
                            <CgCloseR />
                          </span>
                        </div>
                      )
                      : null;
                  })
                }
              </Descriptions.Item>
            )
            : null          
        }
        <Descriptions.Item label={isCreate ? `* ${t('archive.exam.column7')}` : t('archive.learn.column6')} span={4}>
          {
            files.length > 0
              ? files.map((file: any, index: number) => (
                <div className="flex mb-2" key={index}>
                  <FileUpload
                    file={files[index]}
                    setFile={(file: any) => onSetFile(index, file)}
                  />
                  <div className="comm-btn__area">
                    <Button
                      className="btn-basic small gray ml-2"
                      onClick={() => onDeleteFile(index)}
                      text={t('common.button.delete')}
                    />
                  </div>
                </div>
              ))
              : null
          }
          <div className="comm-btn__area">
            <Button
              className="btn-basic pink-line"
              onClick={onAddFile}
              text={t('common.button.add')}
            />
          </div>
        </Descriptions.Item>
      </Descriptions>

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            onClick={onSubmit}
            className="btn-basic pink"
            text={t('common.button.save')}
          />
          <Button
            onClick={() => setConfirmCancelVisible(true)}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>

      {confirmCancelVisible &&
        <ConfirmModal
          visible={confirmCancelVisible}
          setVisible={setConfirmCancelVisible}
          onConfirm={onCancel}
          titleText={t('archive.learn.title4')}
          messageText={t('archive.learn.text1')}
          confirmText={t('common.option.yes')}
          cancelText={t('common.option.no')}
          width={450}
        />
      }
    </div>
  );
}

export default ArchiveEditor;
